import IconArrowDown from '@listening/assets/astro/icons/icon-arrow-down.svg?react';
import IconPause from '@listening/assets/astro/icons/icon-pause.svg?react';
import IconPauseWhite from '@listening/assets/astro/icons/icon-pause-white.svg?react';
import IconPlay from '@listening/assets/astro/icons/icon-play.svg?react';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as reactUseAudioPlayer from 'react-use-audio-player';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import {
  knownVoiceDisplayName,
  resolveVoiceInput,
  type VoiceDisplayName,
} from '@listening/api-contract/schemas';
import { Data, Effect } from 'effect';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import { Spinner } from '@/components/shared-ui/spinner';
import { voiceLangToLangLabel } from '@/lib/utils';
import { useVoicesList } from '@listening/frontend/query/voices';
import { eq } from '@/runtimes/effect-query';
import * as m from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';

function getPlaceholders() {
  return [
    m.try_magic_placeholder_1(),
    m.try_magic_placeholder_2(),
    m.try_magic_placeholder_3(),
    m.try_magic_placeholder_4(),
    m.try_magic_placeholder_5(),
  ];
}

function VoiceDropdown({
  selectedVoice,
  onVoiceSelect,
  isMainAudioPlaying,
  isProcessing,
  onVoiceSamplePlayingChange,
}: {
  selectedVoice: VoiceDisplayName | null;
  onVoiceSelect: (voice: VoiceDisplayName) => void;
  isMainAudioPlaying: boolean;
  isProcessing: boolean;
  className?: string;
  onVoiceSamplePlayingChange?: (isPlaying: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [playingVoice, setPlayingVoice] = useState<VoiceDisplayName | null>(
    null,
  );
  const audioPlayer = reactUseAudioPlayer.useAudioPlayer();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const voiceOptionsQuery = useVoicesList();
  const voiceOptions = voiceOptionsQuery.data ?? [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (playingVoice) {
          audioPlayer.pause();
          setPlayingVoice(null);
          onVoiceSamplePlayingChange?.(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [audioPlayer, onVoiceSamplePlayingChange, playingVoice]);

  // Stop voice sample playback when main audio starts playing
  useEffect(() => {
    if (isMainAudioPlaying && playingVoice) {
      audioPlayer.pause();
      setPlayingVoice(null);
      onVoiceSamplePlayingChange?.(false);
    }
  }, [
    isMainAudioPlaying,
    audioPlayer,
    playingVoice,
    onVoiceSamplePlayingChange,
  ]);

  const handleVoicePlayClick = (
    voice: VoiceDisplayName,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    if (playingVoice === voice) {
      audioPlayer.pause();
      setPlayingVoice(null);
      onVoiceSamplePlayingChange?.(false);
      return;
    }

    if (isMainAudioPlaying || isProcessing) {
      return; // Prevent playing new demo while main audio is active or processing
    }

    const samplePath = voiceOptions.find(
      (voiceOption) => voiceOption.name === voice,
    )?.sample_url;

    if (samplePath) {
      if (audioPlayer.isPlaying) {
        audioPlayer.pause();
      }

      setPlayingVoice(voice);
      onVoiceSamplePlayingChange?.(true);

      audioPlayer.load(samplePath, {
        format: 'mp3',
        autoplay: true,
        html5: true,
        onend: () => {
          setPlayingVoice(null);
          onVoiceSamplePlayingChange?.(false);
        },
      });
    }
  };

  // Cleanup audio when dropdown closes
  useEffect(() => {
    if (!isOpen && playingVoice) {
      audioPlayer.pause();
      setPlayingVoice(null);
      onVoiceSamplePlayingChange?.(false);
    }
  }, [isOpen, audioPlayer, playingVoice, onVoiceSamplePlayingChange]);

  return (
    <div
      className='relative flex w-full justify-center sm:w-auto sm:justify-start'
      ref={dropdownRef}
    >
      <button
        className={`flex w-full items-center justify-center rounded-[45px] border-[1.5px] border-neutral-280/20 px-4 py-3 transition-colors duration-200 sm:justify-between ${
          isProcessing
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer hover:bg-gray-50'
        }`}
        disabled={isProcessing}
        onClick={() => {
          if (!isProcessing) {
            // If closing the dropdown, stop any playing voice samples
            if (isOpen && playingVoice) {
              audioPlayer.pause();
              setPlayingVoice(null);
              onVoiceSamplePlayingChange?.(false);
            }
            setIsOpen(!isOpen);
          }
        }}
        type='button'
      >
        <div className='flex items-center gap-2'>
          <img
            alt={selectedVoice ?? ''}
            className='size-8 rounded-full object-cover'
            height={32}
            src={
              selectedVoice
                ? voiceOptions.find((voice) => voice.name === selectedVoice)
                    ?.image_url
                : ''
            }
            width={32}
          />
          <span>{selectedVoice}</span>
        </div>
        <IconArrowDown
          className={`ml-2 size-7 transform transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className='absolute top-[calc(100%+0.5rem)] left-1/2 z-200 w-[350px] -translate-x-1/2 rounded-lg border border-neutral-280/20 bg-white p-2 shadow-lg sm:left-0 sm:translate-x-0'>
          {voiceOptions.map((voice) => {
            const voiceData = voice;
            const voiceGenderLabel =
              voiceData.gender === 'male'
                ? m.settings_voice_male()
                : m.settings_voice_female();
            const softnessMap: Record<string, () => string> = {
              moderate: () => m.settings_voice_moderate(),
              soft: () => m.settings_voice_soft(),
              deep: () => m.settings_voice_deep(),
            };
            const softness = voiceData.softness ?? 'moderate';
            const capitalizedSoftness =
              softnessMap[softness]?.() ??
              softness.charAt(0).toUpperCase() + softness.slice(1);
            const voiceLangLabel =
              voiceData.lang !== 'en'
                ? voiceLangToLangLabel(voiceData.lang)
                : undefined;
            // const description = `${
            //   voiceData.gender === 'male' ? 'Male' : 'Female'
            // } • ${capitalizedSoftness}`;
            const description = [
              voiceGenderLabel,
              capitalizedSoftness,
              voiceLangLabel,
            ]
              .filter(Boolean)
              .join(' • ');
            return (
              <div
                className={`flex w-full items-center justify-between rounded-lg p-2 transition-colors ${
                  isProcessing
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-gray-50'
                }`}
                key={voice.name}
                onClick={() => {
                  if (!isProcessing) {
                    onVoiceSelect(voice.name);
                    setIsOpen(false);
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isProcessing) {
                    onVoiceSelect(voice.name);
                    setIsOpen(false);
                  }
                }}
                role='button'
                tabIndex={0}
              >
                <div className='flex w-full items-center justify-between px-2'>
                  <div className='flex items-center gap-3'>
                    <img
                      alt={voice.name}
                      className='size-12 rounded-full object-cover'
                      height={48}
                      src={voice.image_url}
                      width={48}
                    />
                    <div className='flex flex-col items-start'>
                      <span className='text-base font-medium text-gray-900'>
                        {voice.name}
                      </span>
                      <span className='text-left text-sm text-gray-500'>
                        {description}
                      </span>
                    </div>
                  </div>
                  <button
                    className={`flex size-8 items-center justify-center rounded-full bg-gray-100 transition-colors ${
                      playingVoice === voice.name
                        ? 'cursor-pointer hover:bg-gray-200'
                        : isMainAudioPlaying || isProcessing
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer hover:bg-gray-200'
                    }`}
                    disabled={
                      !playingVoice && (isMainAudioPlaying || isProcessing)
                    }
                    onClick={(e) => {
                      handleVoicePlayClick(voice.name, e);
                    }}
                    type='button'
                  >
                    {playingVoice === voice.name ? (
                      <IconPause className='size-4' />
                    ) : (
                      <IconPlay className='size-4' />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function useTextAnimator() {
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const currentIndex = useRef(0);
  const charIndex = useRef(0);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const placeholders = getPlaceholders();

    const typeText = () => {
      const placeholder = placeholders[currentIndex.current];
      if (!placeholder) return;

      if (charIndex.current < placeholder.length) {
        setCurrentPlaceholder(placeholder.slice(0, charIndex.current + 1));
        charIndex.current += 1;
        timeoutRef.current = window.setTimeout(typeText, 100);
      } else {
        timeoutRef.current = window.setTimeout(() => {
          timeoutRef.current = window.setTimeout(eraseText, 2000);
        }, 1000);
      }
    };

    const eraseText = () => {
      if (charIndex.current > 0) {
        charIndex.current -= 1;
        setCurrentPlaceholder((current) => current.slice(0, charIndex.current));
        timeoutRef.current = window.setTimeout(eraseText, 50);
      } else {
        currentIndex.current = (currentIndex.current + 1) % placeholders.length;
        timeoutRef.current = window.setTimeout(typeText, 500);
      }
    };

    // Start the animation
    timeoutRef.current = window.setTimeout(typeText, 100);

    return () => {
      if (timeoutRef.current !== undefined) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []); // Only run once on mount

  return currentPlaceholder;
}

export function TryListening({ children }: { children?: React.ReactNode }) {
  return (
    <reactUseAudioPlayer.AudioPlayerProvider>
      <TryListeningInternal>{children}</TryListeningInternal>
    </reactUseAudioPlayer.AudioPlayerProvider>
  );
}

// Client-side validation failures for the demo, carried in the mutation's
// typed error channel next to the API's own errors (BadRequestError, ...).
// The alert maps each tag to localized copy via `error.match` — never render
// `.message`, which for defects is `Cause.pretty(cause)` (a stack trace).
// A defect carries no tag, so it lands in `OrElse`.
class DemoNoTextProvidedError extends Data.TaggedError(
  'DemoNoTextProvidedError',
) {}
class DemoTextTooLongError extends Data.TaggedError('DemoTextTooLongError') {}
class DemoUnknownVoiceError extends Data.TaggedError('DemoUnknownVoiceError') {}

// Thrown synchronously by playHandler while a request is in flight; the play
// button is disabled then, so this only guards programmatic calls.
class DemoTtsProcessingError extends Error {
  constructor() {
    super(m.try_magic_error_processing());
  }
}

function TryListeningInternal({ children }: { children?: React.ReactNode }) {
  const { isInFreePlanCountry } = useFreePlan();
  const initialText = isInFreePlanCountry
    ? m.try_magic_initial_text_free()
    : m.try_magic_initial_text();
  const defaultVoice =
    getLocale() === 'es'
      ? knownVoiceDisplayName('Carmen')
      : knownVoiceDisplayName('Scarlett');
  const [text, setText] = useState<string>(initialText);
  const [selectedVoice, setSelectedVoice] = useState(defaultVoice);
  const [showError, setShowError] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const errorTimeoutRef = useRef<number | undefined>(undefined);
  const MAX_LENGTH = 500;
  const [lastProcessedText, setLastProcessedText] = useState('');
  const [lastProcessedVoice, setLastProcessedVoice] = useState(defaultVoice);
  const [isVoiceSamplePlaying, setIsVoiceSamplePlaying] = useState(false);
  // Track the current audio source: 'main' or 'sample'
  const [currentAudioSource, setCurrentAudioSource] = useState<
    'main' | 'sample' | null
  >(null);

  const animatedPlaceholder = useTextAnimator();

  // Regular expression to match English letters, numbers, spaces, and basic punctuation
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    // Regular expression to match English letters, numbers, spaces, and basic punctuation
    const validText = newText.replaceAll(/[^a-zA-Z0-9\s.,!?'"()\n\r-]/g, '');
    setText(validText);
  };

  useEffect(() => {
    const timeout = errorTimeoutRef.current;
    return () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, []);

  const audioPlayer = reactUseAudioPlayer.useAudioPlayerContext();
  const [isPlaying, setIsPlaying] = useState(false);

  // Update isPlaying state when audio player state changes
  useEffect(() => {
    if (audioPlayer.isPlaying) {
      // If audio started playing
      if (currentAudioSource === 'main') {
        // If it's main audio, update the state
        setIsPlaying(true);
      }
    } else if (currentAudioSource === 'main') {
      // If audio stopped playing and it was main audio, update the playing state but keep the source
      setIsPlaying(false);
      // Only reset currentAudioSource if the audio ended naturally (position is at the end)
      // This allows us to resume from the same position when paused
      if (audioPlayer.getPosition() >= audioPlayer.duration - 0.1) {
        setCurrentAudioSource(null);
      }
    } else if (currentAudioSource === 'sample') {
      // If audio stopped playing and it was a voice sample, update the state
      setIsVoiceSamplePlaying(false);
      setCurrentAudioSource(null);
    }
  }, [
    audioPlayer.isPlaying,
    audioPlayer.getPosition,
    audioPlayer.duration,
    audioPlayer,
    currentAudioSource,
  ]);

  const loadAndPlayMutation = useMutation({
    ...eq.mutationOptions({
      mutationFn: (args: { text: string; voice: VoiceDisplayName }) =>
        Effect.gen(function* () {
          if (!args.text.trim()) {
            return yield* new DemoNoTextProvidedError();
          }
          if (args.text.length > MAX_LENGTH) {
            return yield* new DemoTextTooLongError();
          }

          // The dropdown tracks voices by display name ("Emma"); the
          // contract payload is typed as VoiceModelId ("bf_v0emma"), so
          // resolve before the client encodes the request.
          const voiceModelId = resolveVoiceInput(args.voice);
          if (voiceModelId === undefined) {
            return yield* new DemoUnknownVoiceError();
          }

          if (audioPlayer.isPlaying) {
            audioPlayer.pause();
          }
          if (isVoiceSamplePlaying) {
            setIsVoiceSamplePlaying(false);
          }
          setCurrentAudioSource('main');
          setIsPlaying(true);

          const api = yield* ApiClient;
          const data = yield* api.Tts.DemoText({
            payload: { text: args.text, voice: voiceModelId },
          });

          setLastProcessedText(args.text);
          setLastProcessedVoice(args.voice);

          window.dispatchEvent(new CustomEvent('stopAllWavesurfers'));

          audioPlayer.load(data.tts.url, {
            format: 'mp3',
            autoplay: true,
            html5: true,
            onload: () => {
              setIsPlaying(true);
              setCurrentAudioSource('main');
            },
            onend: () => {
              setIsPlaying(false);
              setCurrentAudioSource(null);
            },
          });
          return data.tts;
        }),
    }),
    onError: () => {
      setIsPlaying(false);
      setCurrentAudioSource(null);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
    },
  });

  const { mutate } = loadAndPlayMutation;
  const isLoading = loadAndPlayMutation.isPending || audioPlayer.isLoading;

  // Reset audio when text changes
  const audioPlayerRef = useRef(audioPlayer);

  useEffect(() => {
    if (audioPlayerRef.current.isReady) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
      setCurrentAudioSource(null);
    }
  }, []);

  const playHandler = useCallback(
    (voice?: VoiceDisplayName) => {
      const playVoice = voice ?? selectedVoice;

      if (isLoading) {
        throw new DemoTtsProcessingError();
      }

      if (isVoiceSamplePlaying || currentAudioSource === 'sample') {
        audioPlayer.pause();
        setIsVoiceSamplePlaying(false);

        const hasContentChanged =
          text !== lastProcessedText || playVoice !== lastProcessedVoice;

        if (!hasContentChanged && lastProcessedText && audioPlayer.isReady) {
          setCurrentAudioSource('main');
          setIsPlaying(true);
          audioPlayer.play();
          return;
        }
        setCurrentAudioSource(null);
      }

      if (
        audioPlayer.isPlaying &&
        (currentAudioSource === 'main' || isPlaying)
      ) {
        audioPlayer.pause();
        setIsPlaying(false);
        return;
      }

      const hasContentChanged =
        text !== lastProcessedText || playVoice !== lastProcessedVoice;

      if (hasContentChanged || currentAudioSource !== 'main') {
        mutate({
          text,
          voice: playVoice,
        });
        return;
      }

      audioPlayer.play();
      setIsPlaying(true);
      setCurrentAudioSource('main');
    },
    [
      mutate,
      selectedVoice,
      text,
      isLoading,
      isPlaying,
      audioPlayer,
      lastProcessedText,
      lastProcessedVoice,
      isVoiceSamplePlaying,
      currentAudioSource,
    ],
  );

  return (
    <section
      aria-labelledby='try-listening-heading'
      className='z-50 pt-6 text-center sm:pt-16 md:pt-24'
      id='try-listening-section'
    >
      <h2
        className='mb-4 text-4xl font-bold sm:mb-6 sm:text-5xl md:text-6xl lg:text-7xl'
        id='try-listening-heading'
      >
        {m.try_magic_heading()}
      </h2>
      <p className='mx-auto mb-8 max-w-4xl px-4 text-sm text-neutral-665 sm:mb-10 sm:text-lg md:mb-12'>
        {m.try_magic_subtitle()}
      </p>

      <div className='flex flex-col items-center gap-4 px-4'>
        <div className='relative h-[200px] w-full rounded-[24px] border-12 border-steel-140 bg-white shadow-lg sm:h-[225px] sm:w-4/5 md:h-[250px] md:w-2/3 lg:h-[275px] lg:w-1/2'>
          <textarea
            className='size-full resize-none rounded-lg p-4 text-sm text-neutral-665 outline-hidden sm:text-base'
            maxLength={MAX_LENGTH}
            onBlur={() => {
              setIsInputFocused(false);
            }}
            onChange={handleTextChange}
            onFocus={() => {
              setIsInputFocused(true);
            }}
            placeholder={isInputFocused ? '' : animatedPlaceholder}
            value={text}
          />
        </div>

        <div className='mt-2 flex w-full flex-col items-center justify-between gap-4 sm:w-4/5 sm:flex-row sm:gap-2 md:w-2/3 lg:w-1/2'>
          <VoiceDropdown
            className='w-full sm:w-auto'
            isMainAudioPlaying={isPlaying}
            isProcessing={isLoading}
            onVoiceSamplePlayingChange={(isPlaying) => {
              setIsVoiceSamplePlaying(isPlaying);
              if (isPlaying) {
                // When starting to play a voice sample, remember that we were playing main audio
                // but don't reset the currentAudioSource if we're stopping a voice sample
                const wasPlayingMainAudio = currentAudioSource === 'main';
                setCurrentAudioSource('sample');

                // Store the fact that we were playing main audio before
                if (wasPlayingMainAudio) {
                  console.log('Remembering that main audio was playing');
                  // We could store this in a ref or state if needed
                }
              } else if (currentAudioSource === 'sample') {
                // When stopping a voice sample, don't automatically reset the audio source
                // This will be handled by the playHandler based on whether we should resume main audio
                console.log('Voice sample stopped, maintaining audio state');
              }
            }}
            onVoiceSelect={(voiceItem) => {
              setSelectedVoice(voiceItem);
            }}
            selectedVoice={selectedVoice}
          />

          <div className='flex w-full items-center gap-4 sm:w-auto'>
            <span
              aria-live='polite'
              className='text-sm text-gray-500'
              id='character-count'
            >
              {m.try_magic_characters({
                count: text.length.toString(),
                max: MAX_LENGTH.toString(),
              })}
            </span>

            <div className='relative w-full sm:w-auto'>
              <button
                aria-label={isPlaying ? 'Pause playback' : 'Start playback'}
                className='flex w-full items-center justify-center gap-2 rounded-full bg-royal-50 px-6 py-3 text-base text-white transition-colors duration-200 hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:px-8 sm:text-lg'
                disabled={isLoading}
                onClick={() => {
                  playHandler();
                }}
                type='button'
              >
                {isLoading ? (
                  <Spinner />
                ) : isPlaying ? (
                  <IconPauseWhite aria-hidden='true' className='size-3' />
                ) : (
                  <IconPlay aria-hidden='true' className='size-4' />
                )}
                <span>
                  {isLoading
                    ? m.loading()
                    : isPlaying
                      ? m.audio_playing()
                      : m.common_play()}
                </span>
              </button>

              {showError && loadAndPlayMutation.error && (
                <div
                  aria-live='assertive'
                  className='absolute top-full left-1/2 mt-2 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600 shadow-xs ring-1 ring-red-500/10 sm:left-0 sm:max-w-none sm:translate-x-0'
                  role='alert'
                >
                  <div className='flex items-center gap-1.5'>
                    <ErrorIcon aria-hidden='true' className='size-4' />
                    {loadAndPlayMutation.error.match({
                      DemoNoTextProvidedError: () =>
                        m.try_magic_error_no_text(),
                      DemoTextTooLongError: () =>
                        m.try_magic_error_too_long({
                          max: MAX_LENGTH.toString(),
                        }),
                      DemoUnknownVoiceError: () => m.try_magic_error_api(),
                      BadRequestError: () => m.try_magic_error_api(),
                      OrElse: () => m.try_magic_error_unexpected(),
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {children}
    </section>
  );
}

// Icon components
function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M12 17a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm0-8.75a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75ZM12 22c-5.523 0-10-4.477-10-10S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10Zm0-1.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17Z'
        fill='currentColor'
      />
    </svg>
  );
}
